import { ReviewDocument } from '@util/types/firestore/review';
import { OrderDocument } from '@util/types/firestore/order';
import {
  collection,
  CollectionReference,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '@util/firebase';
import { EDIT_WINDOW_MS } from './review.constants';

const reviewsRef = collection(
  db,
  'reviews'
) as CollectionReference<ReviewDocument>;

/**
 * Check if a review should be visible based on blind period rules
 * @param review - The review to check
 * @param order - The order associated with this review (to check dispute status)
 * @returns boolean - true if review should be displayed
 */
export const isReviewVisible = (
  review: ReviewDocument,
  order?: OrderDocument
): boolean => {
  // If no reveal_date set (legacy review), it's visible
  if (!review.reveal_date) {
    return true;
  }

  // If order has open dispute (total_state === 3), hide review
  if (order?.total_state === 3) {
    return false;
  }

  // Compute visibility: now >= reveal_date
  return Date.now() >= review.reveal_date;
};

/**
 * Check if both parties have submitted reviews for an order
 * @param order_id - The order ID
 * @returns { buyerReviewed: boolean, sellerReviewed: boolean, bothReviewed: boolean }
 */
export const getReviewStatusForOrder = async (order_id: string): Promise<{
  buyerReviewed: boolean;
  sellerReviewed: boolean;
  bothReviewed: boolean;
}> => {
  // Query for buyer review
  const buyerQuery = query(
    reviewsRef,
    where('order_id', '==', order_id),
    where('reviewer_role', '==', 'buyer')
  );

  // Query for seller review
  const sellerQuery = query(
    reviewsRef,
    where('order_id', '==', order_id),
    where('reviewer_role', '==', 'seller')
  );

  const [buyerSnapshot, sellerSnapshot] = await Promise.all([
    getDocs(buyerQuery),
    getDocs(sellerQuery),
  ]);

  const buyerReviewed = !buyerSnapshot.empty;
  const sellerReviewed = !sellerSnapshot.empty;

  return {
    buyerReviewed,
    sellerReviewed,
    bothReviewed: buyerReviewed && sellerReviewed,
  };
};

/**
 * Check if a review can be edited
 * @param review - The review to check
 * @returns { canEdit: boolean, canEditRating: boolean, reason?: string }
 */
export const getReviewEditability = (review: ReviewDocument): {
  canEdit: boolean;
  canEditRating: boolean;
  reason?: string;
} => {
  const editWindowEnd = (review.created_at ?? 0) + EDIT_WINDOW_MS;

  // Check if within edit window
  if (Date.now() > editWindowEnd) {
    return {
      canEdit: false,
      canEditRating: false,
      reason: 'Edit window has expired',
    };
  }

  // Check if in blind period (can edit text but not rating)
  const isInBlindPeriod = review.reveal_date && Date.now() < review.reveal_date;

  return {
    canEdit: true,
    canEditRating: !isInBlindPeriod,
    reason: isInBlindPeriod
      ? 'Rating cannot be changed during blind period'
      : undefined,
  };
};

/**
 * Filter reviews to only include visible ones
 * @param reviews - Array of reviews
 * @param orders - Map of order_id to OrderDocument for dispute checking
 * @returns Array of visible reviews
 */
export const filterVisibleReviews = (
  reviews: ReviewDocument[],
  orders?: Map<string, OrderDocument>
): ReviewDocument[] => {
  return reviews.filter((review) => {
    const order = orders?.get(review.order_id);
    return isReviewVisible(review, order);
  });
};

/**
 * Get time remaining in blind period
 * @param review - The review to check
 * @returns number - milliseconds until reveal, or 0 if already revealed
 */
export const getBlindPeriodRemaining = (review: ReviewDocument): number => {
  if (!review.reveal_date) return 0;
  const remaining = review.reveal_date - Date.now();
  return remaining > 0 ? remaining : 0;
};

/**
 * Format blind period remaining as human-readable string
 * @param ms - milliseconds remaining
 * @returns string like "12 days" or "3 hours"
 */
export const formatBlindPeriodRemaining = (ms: number): string => {
  if (ms <= 0) return 'Review is now visible';

  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

  if (days > 0) {
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'}`;
};
